import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { Button } from '../ui/Button';
import { NumericInput } from '../common/NumericInput';
import { QuickCreateChipSelect } from './QuickCreateChipSelect';
import { QuickCreateDateControl } from './QuickCreateDateControl';
import { QuickCreateField } from './QuickCreateField';
import { QuickCreateHeroName } from './QuickCreateHeroName';
import { DatePickerSheet } from './QuickCreatePickers';
import { useGoalSheet } from '../../features/quickCreate/SheetProviders';
import { useCreateGoalMutation } from '../../features/goals/queries';
import { useGuardedBottomSheetClose } from '../../hooks/useGuardedBottomSheetClose';
import { parsePtNumber } from '../../utils/money';
import { QUICK_ALLOCATION_OPTIONS } from '../../utils/quickCreateAllocation';
import { formatQuickMonthYear, todayISO, toLocalISODate } from '../../utils/quickCreateDates';
import pt from '../../locales/pt';

function defaultTargetDate() {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return toLocalISODate(date);
}

export function QuickGoalSheet() {
  const { isOpen, setOpen, close } = useGoalSheet();
  const createGoal = useCreateGoalMutation();

  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState(defaultTargetDate());
  const [allocation, setAllocation] = useState('lifestyle');
  const [error, setError] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const openedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    setName('');
    setTargetAmount('');
    setTargetDate(defaultTargetDate());
    setAllocation('lifestyle');
    setError(null);
    setDatePickerOpen(false);
  }, [isOpen]);

  const numericTarget = parsePtNumber(targetAmount);
  const canSubmit =
    Boolean(name.trim()) &&
    numericTarget &&
    numericTarget > 0 &&
    Boolean(targetDate) &&
    !createGoal.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      await createGoal.mutateAsync({
        name: name.trim(),
        target_amount: numericTarget.toFixed(2),
        target_date: targetDate,
        budget_allocation: allocation,
        measurement_type: 'monetary',
      });
      toast.success(pt.quickCreate.goal.createdToast);
      close();
    } catch (err) {
      setError(
        typeof err === 'string'
          ? err
          : err?.detail || err?.target_date || pt.auth.errors.generic,
      );
    }
  };

  const { handleOpenChange, blockDismissProps, wrapChildOpenChange } = useGuardedBottomSheetClose({
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    childOpen: [datePickerOpen],
  });

  return (
    <>
      <BottomSheet open={isOpen} onOpenChange={handleOpenChange}>
        <BottomSheetContent
          title={pt.quickCreate.goal.title}
          layout="bottom"
          {...blockDismissProps}
          footer={
            <Button type="button" className="h-11 w-full rounded-xl" onClick={submit} disabled={!canSubmit}>
              {createGoal.isPending ? pt.common.loading : pt.quickCreate.goal.submit}
            </Button>
          }
        >
          <div className="flex flex-col gap-3.5 pb-2">
            <QuickCreateHeroName
              label={pt.quickCreate.goal.nameLabel}
              placeholder={pt.quickCreate.goal.namePlaceholder}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            <div className="grid min-w-0 grid-cols-2 gap-3">
              <QuickCreateField className="min-w-0" label={pt.quickCreate.goal.targetAmountLabel}>
                <div className="flex min-h-[46px] items-center rounded-xl border border-border bg-card px-3.5">
                  <NumericInput
                    value={targetAmount}
                    onChange={(event) => setTargetAmount(event.target.value)}
                    placeholder="€ 0"
                    className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
                  />
                </div>
              </QuickCreateField>

              <QuickCreateField className="min-w-0" label={pt.quickCreate.goal.targetDateLabel}>
                <QuickCreateDateControl
                  value={targetDate}
                  onClick={() => setDatePickerOpen(true)}
                  formatLabel={formatQuickMonthYear}
                  ariaLabel={pt.quickCreate.goal.targetDateLabel}
                />
              </QuickCreateField>
            </div>

            <QuickCreateField label={pt.quickCreate.allocationLabel}>
              <QuickCreateChipSelect
                options={QUICK_ALLOCATION_OPTIONS}
                value={allocation}
                onChange={setAllocation}
                ariaLabel={pt.quickCreate.allocationLabel}
              />
            </QuickCreateField>

            {error ? (
              <p className="text-sm text-error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </BottomSheetContent>
      </BottomSheet>

      <DatePickerSheet
        open={datePickerOpen}
        onOpenChange={wrapChildOpenChange(setDatePickerOpen)}
        value={targetDate}
        min={todayISO()}
        onSelect={setTargetDate}
      />
    </>
  );
}
